import { useState, useEffect } from 'react'

interface CurrencyInfo {
  symbol: string
  code: string
  originalPrice: string
  salePrice: string
  loading: boolean
}

// Base prices in USD
const BASE_ORIGINAL_USD = 29
const BASE_SALE_USD = 6.79

// Approximate exchange rates relative to USD (updated periodically)
const RATES: Record<
  string,
  { rate: number; symbol: string; decimals: number }
> = {
  USD: { rate: 1, symbol: '$', decimals: 2 },
  MXN: { rate: 17.2, symbol: '$', decimals: 0 },
  COP: { rate: 3950, symbol: '$', decimals: 0 },
  ARS: { rate: 890, symbol: '$', decimals: 0 },
  CLP: { rate: 920, symbol: '$', decimals: 0 },
  PEN: { rate: 3.75, symbol: 'S/', decimals: 2 },
  BOB: { rate: 6.9, symbol: 'Bs.', decimals: 2 },
  PYG: { rate: 7400, symbol: '₲', decimals: 0 },
  UYU: { rate: 39, symbol: '$', decimals: 0 },
  VES: { rate: 36, symbol: 'Bs.', decimals: 2 },
  GTQ: { rate: 7.8, symbol: 'Q', decimals: 2 },
  HNL: { rate: 24.7, symbol: 'L', decimals: 2 },
  NIO: { rate: 36.6, symbol: 'C$', decimals: 2 },
  CRC: { rate: 515, symbol: '₡', decimals: 0 },
  DOP: { rate: 58.5, symbol: 'RD$', decimals: 0 },
  EUR: { rate: 0.92, symbol: '€', decimals: 2 },
  GBP: { rate: 0.79, symbol: '£', decimals: 2 },
  BRL: { rate: 5.0, symbol: 'R$', decimals: 2 },
  CAD: { rate: 1.36, symbol: 'CA$', decimals: 2 },
  AUD: { rate: 1.54, symbol: 'A$', decimals: 2 },
  NZD: { rate: 1.63, symbol: 'NZ$', decimals: 2 },
  CHF: { rate: 0.9, symbol: 'CHF', decimals: 2 },
  SEK: { rate: 10.4, symbol: 'kr', decimals: 0 },
  NOK: { rate: 10.6, symbol: 'kr', decimals: 0 },
  DKK: { rate: 6.9, symbol: 'kr', decimals: 0 },
  PLN: { rate: 4.0, symbol: 'zł', decimals: 2 },
  CZK: { rate: 23.2, symbol: 'Kč', decimals: 0 },
  HUF: { rate: 360, symbol: 'Ft', decimals: 0 },
  RON: { rate: 4.6, symbol: 'lei', decimals: 2 },
  BGN: { rate: 1.8, symbol: 'лв', decimals: 2 },
  HRK: { rate: 6.9, symbol: 'kn', decimals: 2 },
  INR: { rate: 83, symbol: '₹', decimals: 0 },
  PHP: { rate: 56, symbol: '₱', decimals: 0 },
  IDR: { rate: 15700, symbol: 'Rp', decimals: 0 },
  MYR: { rate: 4.7, symbol: 'RM', decimals: 2 },
  SGD: { rate: 1.34, symbol: 'S$', decimals: 2 },
  THB: { rate: 35, symbol: '฿', decimals: 0 },
  VND: { rate: 24500, symbol: '₫', decimals: 0 },
  ZAR: { rate: 18.7, symbol: 'R', decimals: 2 },
  NGN: { rate: 1550, symbol: '₦', decimals: 0 },
  KES: { rate: 130, symbol: 'KSh', decimals: 0 },
  GHS: { rate: 12.5, symbol: 'GH₵', decimals: 2 },
  EGP: { rate: 30.9, symbol: 'E£', decimals: 2 },
  MAD: { rate: 10.1, symbol: 'MAD', decimals: 2 },
  AED: { rate: 3.67, symbol: 'AED', decimals: 2 },
  SAR: { rate: 3.75, symbol: 'SAR', decimals: 2 },
  ILS: { rate: 3.7, symbol: '₪', decimals: 2 },
  TRY: { rate: 32, symbol: '₺', decimals: 2 },
  JPY: { rate: 149, symbol: '¥', decimals: 0 },
  KRW: { rate: 1330, symbol: '₩', decimals: 0 },
  CNY: { rate: 7.24, symbol: '¥', decimals: 2 },
  HKD: { rate: 7.82, symbol: 'HK$', decimals: 2 },
  TWD: { rate: 31.5, symbol: 'NT$', decimals: 0 },
}

function formatPrice(
  amount: number,
  symbol: string,
  decimals: number,
  code: string,
): string {
  const formatted =
    decimals === 0
      ? Math.round(amount).toLocaleString()
      : amount.toFixed(decimals)
  // Put symbol after for certain currencies
  const symbolAfter = [
    'EUR',
    'SEK',
    'NOK',
    'DKK',
    'PLN',
    'CZK',
    'HUF',
    'RON',
    'BGN',
    'HRK',
  ]
  if (symbolAfter.includes(code)) return `${formatted} ${symbol}`
  return `${symbol}${formatted}`
}

export function useCurrency(): CurrencyInfo {
  const [info, setInfo] = useState<CurrencyInfo>({
    symbol: '$',
    code: 'USD',
    originalPrice: '$29',
    salePrice: '$6.79',
    loading: true,
  })

  useEffect(() => {
    let cancelled = false

    async function detect() {
      try {
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000),
        })
        if (!res.ok) throw new Error('geo failed')
        const data = await res.json()
        const currencyCode: string = data.currency || 'USD'

        if (cancelled) return

        const entry = RATES[currencyCode] ?? RATES['USD']
        const { rate, symbol, decimals } = entry

        const originalConverted = BASE_ORIGINAL_USD * rate
        const saleConverted = BASE_SALE_USD * rate

        setInfo({
          symbol,
          code: currencyCode,
          originalPrice: formatPrice(
            originalConverted,
            symbol,
            decimals,
            currencyCode,
          ),
          salePrice: formatPrice(saleConverted, symbol, decimals, currencyCode),
          loading: false,
        })
      } catch {
        if (!cancelled) {
          setInfo((prev) => ({ ...prev, loading: false }))
        }
      }
    }

    detect()
    return () => {
      cancelled = true
    }
  }, [])

  return info
}
